import type { LayerKey } from '../store/types';

/**
 * بنية قائمة الطبقات بترتيب Zoom Earth نفسه، مع متغيّرات فرعية لكل طبقة رئيسية
 * (الرياح → سرعة/هبّات، الحرارة → فعلي/إحساس، الرطوبة → نسبية/ندى). الطبقات الإضافية
 * (لدينا ولا توجد في Zoom Earth) أسفل القائمة.
 *
 * مجموعة الطبقات وبنية المجموعات يحدّدها الكود؛ الأدمن يتحكّم في التفعيل
 * والتسميات والألوان والترتيب عبر إعدادات قاعدة البيانات.
 */
/**
 * التسميات نفسها ليست هنا: تُقرأ من القاموس بمفاتيح `layerGroup.<parentId>` و
 * `layerVariant.<id>` عبر useLayerLabels — فتتبع لغة الواجهة وتخصيص الأدمن معاً.
 */
export interface VariantDef { id: LayerKey; }
export interface GroupDef { parentId: LayerKey; variants?: VariantDef[]; }

export const LAYER_GROUPS: GroupDef[] = [
    { parentId: 'satellite' },
    { parentId: 'radar' },
    { parentId: 'precipitation' },
    // بجوار الأمطار لا تحت «الرصد»: حقل نموذج (يُصفّى بـmeta.layers، وGFS وحده يولّده).
    { parentId: 'radar-sim' },
    { parentId: 'wind', variants: [
        { id: 'wind' },
        { id: 'wind-gusts' },
    ] },
    { parentId: 'temperature', variants: [
        { id: 'temperature' },
        { id: 'feels-like' },
        { id: 'wet-bulb' },
    ] },
    { parentId: 'k-index', variants: [
        { id: 'k-index' },
        { id: 'o-index' },
    ] },
    { parentId: 'humidity', variants: [
        { id: 'humidity' },
        { id: 'dewpoint' },
    ] },
    { parentId: 'pressure' },
    // مجموعة بمفردها: حقل نموذج (فتُصفّى بـmeta.layers) لكنه لا يشترك في سلّم
    // أو مرجع مع حرارة السطح، فدمجه في مجموعتها كان سيوحي بأنهما قراءتان لشيء واحد.
    { parentId: 't850-anomaly' },
    // مجموعة بمتغيّرين كالحرارة (فعلي/إحساس): نفس البيانات بمرجعين مختلفين.
    { parentId: 'sst-anomaly', variants: [
        { id: 'sst-anomaly' },
        { id: 'sst-anomaly-rel' },
    ] },
];

// طبقات إضافية لدينا (غير موجودة في Zoom Earth) — تظهر أسفل القائمة.
// الأعاصير والحرائق مستقلّة عن قائمة الطبقات: لها أزرار عائمة خاصة.
// طبقات خارج بنية المجموعات. 'sst-anomaly' هنا لا في LAYER_GROUPS لأنها **رصد شهري
// المدى مستقلّ عن النموذج**، فلا تنتمي لأي مجموعة توقّع ولا يصحّ أن تُصفّى بـmeta.layers.
export const EXTRA_LAYER_IDS: LayerKey[] = ['clouds'];

/**
 * القسم الأوّل من لوحة «خرائط الطقس»: **رصدٌ** لا توقّع — ما ترصده الأقمار والرادارات
 * الآن، لا ما يحسبه نموذج. وفصله عن بقيّة الطبقات ليس ترتيباً شكلياً: القارئ يحتاج أن
 * يعرف هل ما يراه قياسٌ وقع فعلاً أم تنبّؤ قد يُخطئ، وخلطهما في شبكة واحدة يُضيّع الفرق.
 * الباقي كلّه — حقول النموذج والغيوم — ينزل تحت «عناصر الطقس».
 */
export const OBSERVATION_GROUP_IDS: LayerKey[] = ['satellite', 'radar'];

/** الترتيب الافتراضي المسطّح لكل الطبقات (يُبذر في قاعدة البيانات). */
export const DEFAULT_LAYER_ORDER: Record<string, { sortOrder: number; groupKey: string }> = {
    satellite:       { sortOrder: 10,  groupKey: 'satellite' },
    radar:           { sortOrder: 20,  groupKey: 'radar' },
    precipitation:   { sortOrder: 30,  groupKey: 'precipitation' },
    'radar-sim':     { sortOrder: 31,  groupKey: 'radar-sim' },
    wind:            { sortOrder: 40,  groupKey: 'wind' },
    'wind-gusts':    { sortOrder: 41,  groupKey: 'wind' },
    temperature:     { sortOrder: 50,  groupKey: 'temperature' },
    'feels-like':    { sortOrder: 51,  groupKey: 'temperature' },
    'wet-bulb':      { sortOrder: 52,  groupKey: 'temperature' },
    'k-index':       { sortOrder: 60,  groupKey: 'storms' },
    'o-index':       { sortOrder: 61,  groupKey: 'storms' },
    humidity:        { sortOrder: 70,  groupKey: 'humidity' },
    dewpoint:        { sortOrder: 71,  groupKey: 'humidity' },
    pressure:        { sortOrder: 80,  groupKey: 'pressure' },
    clouds:          { sortOrder: 90,  groupKey: 'extra' },
    't850-anomaly':  { sortOrder: 55,  groupKey: 't850-anomaly' },
    'sst-anomaly':     { sortOrder: 95,  groupKey: 'sst-anomaly' },
    'sst-anomaly-rel': { sortOrder: 96,  groupKey: 'sst-anomaly' },
    hurricanes:      { sortOrder: 100, groupKey: 'overlay' },
    wildfires:       { sortOrder: 110, groupKey: 'overlay' },
};
